import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Card,
  CardContent,
  Typography,
  Autocomplete,
  Avatar,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useNavigate,  } from 'react-router-dom';
import * as endPoints from '../assets/js/endpoints';
import PageTitle from '../components/PageTitle';
import { useNotification } from '../assets/contexts/notificationContext';
import { Helmet } from 'react-helmet';
import {
  INTRANET_FORM_CARD_SX,
  INTRANET_FORM_GRID_SX,
  INTRANET_FULL_WIDTH_FIELD_SX,
  INTRANET_PAGE_CONTAINER_SX,
  INTRANET_WIDE_FIELD_SX,
} from '../constants/layout';

function Register() {
  const [form, setForm] = useState({
    name: '',
    lastName: '',
    email: '',
    password: '',
    confirm: '',
    role: '',
    service: '',
    category: '',
    bio: '',
    color: '#000000',
    employeeOrder: '',
    active: true,
    showInAbout: true,
  });
  const [image, setImage] = useState(null);
  const [roles, setRoles] = useState([]);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [fileName, setFileName] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [maxEmployeeOrder, setMaxEmployeeOrder] = useState(1);
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  useEffect(() => {
    const fetchData = async () => {
      const [allRoles, allServices, allCategories, allUsers] = await Promise.all([
        endPoints.getAllRoles(),
        endPoints.getAllServices(),
        endPoints.getAllCategories(),
        endPoints.getAllUsers(),
      ]);

      setRoles(allRoles);
      setServices(allServices);
      setCategories(allCategories);

      const usersList = Array.isArray(allUsers) ? allUsers : allUsers?.data || [];
      const aboutUsersCount = usersList.filter(u => u.show_in_about !== false).length;
      setMaxEmployeeOrder(Math.max(aboutUsersCount, 1));
    };
    fetchData();
  }, []);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };
  const handleImageChange = e => {
    if (e.target.files && e.target.files[0]) {
      setImage(e.target.files[0]);
      setFileName(e.target.files[0].name);
    }
  };

  const validatePassword = p => /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/.test(p);

  const handleSubmit = async e => {
    e.preventDefault();
    if (form.password !== form.confirm) {
      showNotification('Las contraseñas no coinciden', 'error');
      return;
    }
    if (!validatePassword(form.password)) {
      showNotification('La contraseña debe tener al menos 8 caracteres, una mayúscula, una minúscula y un número', 'error');
      return;
    }
    const res = await endPoints.registerUser(
      form.name,
      form.lastName,
      form.email,
      form.password,
      form.role,
      form.service,
      form.bio,
      image,
      form.category,
      form.color,
      false,
      form.employeeOrder,
      form.active,
      form.showInAbout,
    );
    if (res.access_token || res.token) {
      showNotification('Usuario registrado', 'success');
      navigate('/admin/usuarios');
    } else if (res.error) {
      showNotification(res.error, 'error');
    } else {
      showNotification('Error registrando usuario', 'error');
    }
  };

  return (
    <>
    <Helmet>
      <title>Crear Usuario | CES</title>
    </Helmet>
    <Container maxWidth="xl" sx={INTRANET_PAGE_CONTAINER_SX}>
      <Card sx={INTRANET_FORM_CARD_SX}>
        <CardContent>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ ...INTRANET_FORM_GRID_SX, mb: 2 }}
          >
            <Box sx={INTRANET_WIDE_FIELD_SX}>
              <PageTitle>Crear Usuario</PageTitle>
            </Box>

        {/* <Box sx={{display: 'flex', justifyContent: 'space-around', gap: 2}}> */}
        <TextField
          label="Nombre"
          name="name"
          value={form.name}
          onChange={handleChange}
          required
          fullWidth
          sx={INTRANET_FULL_WIDTH_FIELD_SX}
        />

        <TextField
          label="Apellidos"
          name="lastName"
          value={form.lastName}
          onChange={handleChange}
          required
          fullWidth
          sx={INTRANET_FULL_WIDTH_FIELD_SX}
        />

        <TextField
          label="Email"
          name="email"
          type="email"
          value={form.email}
          onChange={handleChange}
          required
          fullWidth
          sx={INTRANET_FULL_WIDTH_FIELD_SX}
        />
        {/* </Box> */}

        <TextField
          label="Contraseña"
          name="password"
          type={showPass ? 'text' : 'password'}
          value={form.password}
          onChange={handleChange}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPass(p => !p)} edge="end">
                  {showPass ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          required
          fullWidth
          sx={INTRANET_FULL_WIDTH_FIELD_SX}
        />
        <TextField
          label="Confirmar contraseña"
          name="confirm"
          type={showConfirm ? 'text' : 'password'}
          value={form.confirm}
          onChange={handleChange}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowConfirm(p => !p)} edge="end">
                  {showConfirm ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          required
          fullWidth
          sx={INTRANET_FULL_WIDTH_FIELD_SX}
        />

        <Autocomplete
          options={roles}
          value={roles.find(r => r.name === form.role) || null}
          onChange={(_event, newValue) => {
            setForm(prev => ({ ...prev, role: newValue ? newValue.name : '' }));
          }}
          getOptionLabel={option => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={params => (
            <TextField {...params} label="Rol" required fullWidth sx={INTRANET_FULL_WIDTH_FIELD_SX} />
          )}
        />
        <Autocomplete
          options={categories}
          value={categories.find(c => String(c.id) === String(form.category)) || null}
          onChange={(_event, newValue) => {
            setForm(prev => ({ ...prev, category: newValue ? newValue.id : '' }));
          }}
          getOptionLabel={option => option.name || option.title || ''}
          isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
          renderInput={params => (
            <TextField {...params} label="Categoría" fullWidth sx={INTRANET_FULL_WIDTH_FIELD_SX} />
          )}
        />
        <Autocomplete
          options={services}
          value={services.find(s => String(s.id) === String(form.service)) || null}
          onChange={(_event, newValue) => {
            setForm(prev => ({ ...prev, service: newValue ? newValue.id : '' }));
          }}
          getOptionLabel={option => option.name}
          isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
          renderInput={params => (
            <TextField {...params} label="Servicio" fullWidth sx={INTRANET_FULL_WIDTH_FIELD_SX} />
          )}
        />

        <Box sx={{ ...INTRANET_WIDE_FIELD_SX, display: 'grid', gap: 1 }}>
          <Button variant="contained" component="label">
            Subir foto del usuario
            <input type="file" hidden accept="image/*" onChange={handleImageChange} />
          </Button>
          {fileName && (
            <Typography variant="body2" color="text.secondary">
              {fileName}
            </Typography>
          )}
          {image && (
            <Avatar
              src={URL.createObjectURL(image)}
              alt="Previsualización"
              sx={{ width: 96, height: 96 }}
              variant="rounded"
            />
          )}
        </Box>
        <TextField
          type="color"
          label="Color"
          name="color"
          value={form.color}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
          fullWidth
          sx={INTRANET_FULL_WIDTH_FIELD_SX}
        />

        <TextField
          type="number"
          label="Orden en Quiénes somos"
          name="employeeOrder"
          value={form.employeeOrder}
          onChange={handleChange}
          inputProps={{ min: 1, max: maxEmployeeOrder }}
          helperText={`Máximo actual según usuarios marcados para About Us: ${maxEmployeeOrder}.`}
          fullWidth
          sx={INTRANET_FULL_WIDTH_FIELD_SX}
        />


        <Box sx={{ ...INTRANET_WIDE_FIELD_SX, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <FormControlLabel
            control={<Checkbox name="active" checked={form.active} onChange={handleChange} />}
            label="Activo (Pide tu cita)"
          />
          <FormControlLabel
            control={<Checkbox name="showInAbout" checked={form.showInAbout} onChange={handleChange} />}
            label="Mostrar en About Us"
          />
        </Box>

        <TextField
          label="Bio"
          name="bio"
          multiline
          rows={4}
          value={form.bio}
          onChange={handleChange}
          fullWidth
          sx={INTRANET_WIDE_FIELD_SX}
        />
        <Box sx={{ ...INTRANET_WIDE_FIELD_SX, display: 'flex', justifyContent: 'center' }}>
          <Button type="submit" variant="contained">Registrar</Button>
        </Box>
      </Box>

        </CardContent>
      </Card>
    </Container>
    </>
  );
}
export default Register;
